/**
 * Where the automatic sync keeps what it is doing, so three screens and a
 * shell-level timer can all see it.
 *
 * Deliberately IMPORT-LIGHT: no service, no client, nothing from the three
 * channel modules. Their hooks import THIS to hear when a run has written
 * something, and the runner imports THEM — so anything heavier here would close
 * a cycle.
 *
 * Memory for the results (they describe the tab in front of you), localStorage
 * for exactly two per-browser facts: whether it is paused, and when the last
 * run started. The second is what lets several tabs of one browser share one
 * timer instead of each running its own.
 */
import { useSyncExternalStore } from "react";

export type AutoChannel = "tiktok" | "igcom" | "lazada";

export interface IAutoResult {
  channel: AutoChannel;
  /** ok: it ran. skipped: it chose not to, for a stated reason. failed: it threw. */
  status: "ok" | "skipped" | "failed";
  /** ISO. */
  at: string;
  /** Orders the live window returned. */
  seen: number;
  /** Orders actually written — zero when the store already agreed. */
  written: number;
  /** Why, when status is not ok. "" otherwise. */
  note: string;
}

export interface IAutoState {
  enabled: boolean;
  running: boolean;
  results: Partial<Record<AutoChannel, IAutoResult>>;
}

const ENABLED_KEY = "isyn-autosync-enabled";
const LAST_RUN_KEY = "isyn-autosync-last-run";

const safe = <T,>(fn: () => T, fallback: T): T => {
  try { return fn(); } catch { return fallback; }
};

/** ON unless somebody switched it off in this browser. */
const readEnabled = (): boolean => safe(() => localStorage.getItem(ENABLED_KEY) !== "0", true);

export const readLastRun = (): number =>
  safe(() => Number(localStorage.getItem(LAST_RUN_KEY)) || 0, 0);

export const writeLastRun = (at: number): void =>
  safe(() => localStorage.setItem(LAST_RUN_KEY, String(at)), undefined);

let state: IAutoState = { enabled: readEnabled(), running: false, results: {} };
const listeners = new Set<() => void>();
const wroteListeners = new Set<(channel: AutoChannel) => void>();

const emit = (next: IAutoState): void => {
  state = next;
  listeners.forEach((l) => l());
};

export const getAutoSyncState = (): IAutoState => state;

export const subscribeAutoSync = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
};

export const setAutoSyncEnabled = (enabled: boolean): void => {
  safe(() => localStorage.setItem(ENABLED_KEY, enabled ? "1" : "0"), undefined);
  emit({ ...state, enabled });
};

export const setAutoSyncRunning = (running: boolean): void => emit({ ...state, running });

export const recordAutoResult = (r: IAutoResult): void =>
  emit({ ...state, results: { ...state.results, [r.channel]: r } });

/** Tell the screens that a channel's stored rows just moved. Only called for a
 *  run that WROTE something — an unchanged store has nothing to repaint. */
export const notifyAutoWrote = (channel: AutoChannel): void =>
  wroteListeners.forEach((l) => l(channel));

export const subscribeAutoWrote = (cb: (channel: AutoChannel) => void): (() => void) => {
  wroteListeners.add(cb);
  return () => { wroteListeners.delete(cb); };
};

export const useAutoSyncState = (): IAutoState =>
  useSyncExternalStore(subscribeAutoSync, getAutoSyncState, getAutoSyncState);
